"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import {
  Plus, FolderOpen, X, FileText, CheckSquare, Trash2,
  ChevronRight, RefreshCw, Send, Search, MessageSquare,
  Calendar, Flag, Upload, Download, LayoutGrid, List,
  ChevronDown, Edit3, User
} from "lucide-react";

const API = process.env.NEXT_PUBLIC_API_URL || "http://100.68.12.86:3200";

const COLORS = ["#C9A84C","#3b82f6","#22c55e","#f59e0b","#a855f7","#ec4899","#0F1923","#06b6d4"];

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  active:    { label: "Activo",     color: "#22c55e" },
  paused:    { label: "Pausado",    color: "#f59e0b" },
  completed: { label: "Completado", color: "#3b82f6" },
  archived:  { label: "Archivado",  color: "#9ca3af" },
};

const TASK_STATUS: Record<string, { label: string; color: string }> = {
  pending:  { label: "Pendiente",   color: "#f59e0b" },
  running:  { label: "En progreso", color: "#3b82f6" },
  done:     { label: "Completada",  color: "#22c55e" },
  failed:   { label: "Fallida",     color: "#ef4444" },
};

const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  low:    { label: "Baja",  color: "#9ca3af" },
  medium: { label: "Media", color: "#f59e0b" },
  high:   { label: "Alta",  color: "#ef4444" },
};


// ── Estilos ──────────────────────────────────────────────────
const overlayStyle: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(15,25,35,0.45)",
  zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
  backdropFilter: "blur(3px)",
};
const modalStyle: React.CSSProperties = {
  background: "var(--bg-card)", borderRadius: "20px",
  padding: "32px", width: "620px", maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
};
const inputStyle: React.CSSProperties = {
  padding: "10px 14px", background: "var(--bg)", border: "1px solid var(--border)",
  borderRadius: "10px", fontSize: "13px", color: "var(--text)",
  outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" as const,
};
const btnPrimaryStyle: React.CSSProperties = {
  padding: "10px 20px", background: "var(--bg-dark)", color: "white",
  border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: 500, cursor: "pointer",
};
const btnSecondaryStyle: React.CSSProperties = {
  padding: "10px 20px", background: "var(--bg-muted)", color: "var(--text)",
  border: "1px solid var(--border)", borderRadius: "10px", fontSize: "13px", cursor: "pointer",
};
const iconBtnStyle: React.CSSProperties = {
  width: "32px", height: "32px", borderRadius: "8px",
  background: "var(--bg-muted)", border: "1px solid var(--border)",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer", color: "var(--text-muted)",
};


// ── AssigneeSelect ───────────────────────────────────────────
function AssigneeSelect({ agents, members, agentId, assigneeId, onChange }: {
  agents: any[]; members: any[];
  agentId: any; assigneeId: any;
  onChange: (type: 'agent' | 'member' | 'none', id: string) => void;
}) {
  const value = agentId ? `agent:${agentId}` : assigneeId ? `member:${assigneeId}` : '';
  return (
    <select value={value} onChange={e => {
      const v = e.target.value;
      if (!v) return onChange('none', '');
      const [type, id] = v.split(':');
      onChange(type as 'agent' | 'member', id);
    }} style={inputStyle}>
      <option value="">Sin asignar</option>
      {agents.length > 0 && (
        <optgroup label="🤖 Agentes IA">
          {agents.map(a => <option key={`agent:${a.id}`} value={`agent:${a.id}`}>{a.emoji} {a.name}</option>)}
        </optgroup>
      )}
      {members.length > 0 && (
        <optgroup label="👥 Equipo">
          {members.map(m => <option key={`member:${m.id}`} value={`member:${m.id}`}>{m.emoji} {m.name}</option>)}
        </optgroup>
      )}
    </select>
  );
}

// ── ProjectUploadZone ────────────────────────────────────────
function ProjectUploadZone({ projectId, onUploaded }: { projectId: number; onUploaded: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const doUpload = async (files: FileList) => {
    if (!files.length) return;
    setUploading(true);
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setProgress(`Subiendo ${file.name}...`);
      const fd = new FormData();
      fd.append("file", file); fd.append("title", file.name);
      await fetch(`${API}/projects/${projectId}/docs/upload`, { method: "POST", body: fd });
    }
    setUploading(false); setProgress(""); onUploaded();
  };
  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); doUpload(e.dataTransfer.files); }}
      onClick={() => inputRef.current?.click()}
      style={{ border: `2px dashed ${dragging ? "var(--accent)" : "var(--border)"}`, borderRadius: "10px", padding: "18px", textAlign: "center", cursor: "pointer", background: dragging ? "rgba(201,168,76,0.05)" : "var(--bg)", transition: "all 150ms", marginBottom: "14px" }}
    >
      <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={e => e.target.files && doUpload(e.target.files)} />
      {uploading
        ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}><RefreshCw size={15} color="var(--accent)" className="animate-spin" /><span style={{ fontSize: "12px", color: "var(--text-muted)" }}>{progress}</span></div>
        : <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}><Upload size={15} color={dragging ? "var(--accent)" : "var(--text-light)"} /><span style={{ fontSize: "13px", color: dragging ? "var(--accent)" : "var(--text-muted)" }}>{dragging ? "Suelta aquí" : "Arrastra o haz click para subir archivos"}</span></div>
      }
    </div>
  );
}


// ── TaskPanel (lateral derecho estilo Notion) ─────────────────
function TaskPanel({ task, agents, members, projectId, onClose, onUpdated }: {
  task: any; agents: any[]; members: any[]; projectId: number;
  onClose: () => void; onUpdated: () => void;
}) {
  const [form, setForm] = useState({
    title: task.title || "", description: task.description || "",
    status: task.status || "pending", priority: task.priority || "medium",
    due_date: task.due_date ? task.due_date.split("T")[0] : "",
    agent_id: task.agent_id || "", assignee_id: task.assignee_id || "",
  });
  const [subtasks, setSubtasks] = useState<any[]>(task.subtasks || []);
  const [comments, setComments] = useState<any[]>(task.comments || []);
  const [newSubtask, setNewSubtask] = useState("");
  const [newComment, setNewComment] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await fetch(`${API}/projects/tasks/${task.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...form, agent_id: form.agent_id || null, assignee_id: form.assignee_id || null }),
    });
    setSaving(false); onUpdated();
  };

  const addSubtask = async () => {
    if (!newSubtask.trim()) return;
    const r = await fetch(`${API}/projects/${projectId}/tasks`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: newSubtask, parent_id: task.id, status: "pending" }),
    });
    const sub = await r.json(); setSubtasks(prev => [...prev, sub]); setNewSubtask("");
  };

  const toggleSubtask = async (sub: any) => {
    const ns = sub.status === "done" ? "pending" : "done";
    await fetch(`${API}/projects/tasks/${sub.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: ns }) });
    setSubtasks(prev => prev.map(s => s.id === sub.id ? { ...s, status: ns } : s));
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    const r = await fetch(`${API}/projects/tasks/${task.id}/comments`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content: newComment, author: "Didac" }),
    });
    const c = await r.json(); setComments(prev => [...prev, c]); setNewComment("");
  };

  const deleteTask = async () => {
    if (!confirm("¿Eliminar esta tarea?")) return;
    await fetch(`${API}/projects/tasks/${task.id}`, { method: "DELETE" });
    onUpdated(); onClose();
  };

  const s = TASK_STATUS[form.status] || TASK_STATUS.pending;
  const p = PRIORITY_CONFIG[form.priority] || PRIORITY_CONFIG.medium;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 90, display: "flex" }} onClick={onClose}>
      {/* Overlay semitransparente */}
      <div style={{ flex: 1, background: "rgba(15,25,35,0.25)" }} />
      {/* Panel lateral */}
      <div style={{ width: "480px", maxWidth: "95vw", height: "100vh", background: "var(--bg-card)", borderLeft: "1px solid var(--border)", overflowY: "auto", boxShadow: "-8px 0 40px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: "6px" }}>
            <span style={{ fontSize: "11px", padding: "3px 9px", borderRadius: "20px", background: s.color + "20", color: s.color, fontWeight: 500 }}>{s.label}</span>
            <span style={{ fontSize: "11px", padding: "3px 9px", borderRadius: "20px", background: p.color + "20", color: p.color, fontWeight: 500 }}>{p.label}</span>
          </div>
          <div style={{ display: "flex", gap: "6px" }}>
            <button onClick={deleteTask} style={{ ...iconBtnStyle, color: "#ef4444" }}><Trash2 size={14} /></button>
            <button onClick={onClose} style={iconBtnStyle}><X size={14} /></button>
          </div>
        </div>

        <div style={{ padding: "20px 24px", flex: 1, overflowY: "auto" }}>
          {/* Título */}
          <textarea value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            style={{ width: "100%", fontSize: "20px", fontWeight: 700, background: "none", border: "none", outline: "none", fontFamily: "inherit", color: "var(--text)", resize: "none", lineHeight: 1.3, marginBottom: "16px", boxSizing: "border-box" as const }}
            rows={2} />

          {/* Propiedades en grid compacto estilo Notion */}
          <div style={{ display: "flex", flexDirection: "column", gap: "0", marginBottom: "20px", border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden" }}>
            {[
              { label: "Estado", content: <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={{ ...inputStyle, border: "none", background: "none", padding: "6px 8px" }}>{Object.entries(TASK_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select> },
              { label: "Prioridad", content: <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={{ ...inputStyle, border: "none", background: "none", padding: "6px 8px" }}>{Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select> },
              { label: "Fecha límite", content: <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} style={{ ...inputStyle, border: "none", background: "none", padding: "6px 8px" }} /> },
              { label: "Asignado a", content: <AssigneeSelect agents={agents} members={members} agentId={form.agent_id} assigneeId={form.assignee_id} onChange={(type, id) => setForm(f => ({ ...f, agent_id: type === 'agent' ? id : '', assignee_id: type === 'member' ? id : '' }))} /> },
            ].map(({ label, content }, i) => (
              <div key={label} style={{ display: "flex", alignItems: "center", borderBottom: i < 3 ? "1px solid var(--border)" : "none" }}>
                <div style={{ width: "110px", padding: "8px 12px", fontSize: "12px", color: "var(--text-muted)", fontWeight: 500, flexShrink: 0, borderRight: "1px solid var(--border)", background: "var(--bg)" }}>{label}</div>
                <div style={{ flex: 1, fontSize: "13px" }}>{content}</div>
              </div>
            ))}
          </div>

          {/* Descripción */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 600, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>Descripción</div>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Añade una descripción..." rows={4}
              style={{ ...inputStyle, resize: "vertical", fontSize: "13px" }} />
          </div>

          <button onClick={save} disabled={saving} style={{ ...btnPrimaryStyle, width: "100%", marginBottom: "24px", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px", opacity: saving ? 0.7 : 1 }}>
            {saving ? <RefreshCw size={13} className="animate-spin" /> : null} Guardar cambios
          </button>

          {/* Subtareas */}
          <div style={{ marginBottom: "20px" }}>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 600, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: "6px" }}>
              <CheckSquare size={13} /> Subtareas ({subtasks.filter(s => s.status === "done").length}/{subtasks.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginBottom: "8px" }}>
              {subtasks.map(s => (
                <label key={s.id} style={{ display: "flex", alignItems: "center", gap: "8px", padding: "7px 10px", background: "var(--bg)", borderRadius: "8px", cursor: "pointer" }}>
                  <input type="checkbox" checked={s.status === "done"} onChange={() => toggleSubtask(s)} style={{ cursor: "pointer", accentColor: "var(--accent)" }} />
                  <span style={{ fontSize: "13px", textDecoration: s.status === "done" ? "line-through" : "none", color: s.status === "done" ? "var(--text-muted)" : "var(--text)" }}>{s.title}</span>
                </label>
              ))}
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              <input value={newSubtask} onChange={e => setNewSubtask(e.target.value)} onKeyDown={e => e.key === "Enter" && addSubtask()} placeholder="+ Añadir subtarea..." style={{ ...inputStyle, flex: 1 }} />
              <button onClick={addSubtask} style={{ ...btnPrimaryStyle, padding: "10px 14px" }}><Plus size={14} /></button>
            </div>
          </div>

          {/* Comentarios */}
          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: 600, marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: "6px" }}>
              <MessageSquare size={13} /> Comentarios ({comments.length})
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "8px" }}>
              {comments.map(c => (
                <div key={c.id} style={{ padding: "10px 12px", background: "var(--bg)", borderRadius: "8px", borderLeft: "3px solid var(--accent)" }}>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", marginBottom: "4px", fontWeight: 500 }}>{c.author}</div>
                  <div style={{ fontSize: "13px" }}>{c.content}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "6px" }}>
              <input value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => e.key === "Enter" && addComment()} placeholder="Escribe un comentario..." style={{ ...inputStyle, flex: 1 }} />
              <button onClick={addComment} style={{ ...btnPrimaryStyle, padding: "10px 14px" }}><Send size={14} /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ── AgentChatWidget (estilo Paperclip — flotante) ────────────
function AgentChatWidget({ project, agents, onProjectUpdated }: { project: any; agents: any[]; onProjectUpdated?: () => void }) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState<any>(agents[0] || null);
  const [messages, setMessages] = useState<{ role: string; content: string; ts: string }[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setUnread(false);
      setTimeout(() => inputRef.current?.focus(), 120);
      if (messages.length === 0) {
        const a = selectedAgent;
        setMessages([{ role: "assistant", content: `Hola 👋 Soy ${a?.emoji || ""} ${a?.name || "tu agente"}. ¿En qué puedo ayudarte con "${project.name}"?`, ts: new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) }]);
      }
    }
  }, [open]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const switchAgent = (agentId: string) => {
    const a = agents.find(a => a.id === agentId);
    if (!a) return;
    setSelectedAgent(a);
    setMessages([{ role: "assistant", content: `Hola 👋 Soy ${a.emoji} ${a.name}. ¿En qué puedo ayudarte con "${project.name}"?`, ts: new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) }]);
  };

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input, ts: new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) };
    const currentMessages = [...messages, userMsg];
    setMessages(currentMessages);
    const userInput = input;
    setInput(""); setLoading(true);

    try {
      const systemContext = `Eres ${selectedAgent?.emoji} ${selectedAgent?.name}, agente de Varadero by Maury.\nProyecto: "${project.name}". Descripción: ${project.description || "Sin descripción"}. Estado: ${STATUS_CONFIG[project.status]?.label || project.status}.\nResponde de forma concisa y útil en español.`;
      const r = await fetch(`${API}/agents/${selectedAgent?.id}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userInput,
          systemContext,
          projectId: project.id,
          history: currentMessages.slice(-12).map(m => ({ role: m.role, content: m.content }))
        })
      });
      if (!r.ok) throw new Error("backend error");
      const data = await r.json();
      const reply = data.reply || "...";
      // Si hubo acción, refrescar el proyecto
      if (data.action) onProjectUpdated?.();
      setMessages(prev => [...prev, { role: "assistant", content: reply, ts: new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Error al conectar. Inténtalo de nuevo.", ts: new Date().toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }) }]);
    }
    setLoading(false);
  };

  if (!agents.length) return null;

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          position: "fixed", bottom: "28px", right: "28px",
          width: "52px", height: "52px", borderRadius: "50%",
          background: "var(--bg-dark)", border: "none",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 4px 20px rgba(0,0,0,0.35)", zIndex: 200,
          transition: "transform 0.2s, box-shadow 0.2s",
          fontSize: "20px",
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1.09)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 6px 28px rgba(0,0,0,0.45)"; }}
        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 20px rgba(0,0,0,0.35)"; }}
      >
        {open ? <X size={20} color="white" /> : <span>{selectedAgent?.emoji || "🤖"}</span>}
        {/* Badge no leído */}
        {!open && unread && (
          <span style={{ position: "absolute", top: "2px", right: "2px", width: "16px", height: "16px", borderRadius: "50%", background: "#ef4444", color: "white", fontSize: "10px", fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid var(--bg-dark)" }}>1</span>
        )}
      </button>

      {/* Panel de chat */}
      <div style={{
        position: "fixed", bottom: "92px", right: "28px",
        width: "min(370px, 90vw)", height: "min(500px, 78vh)",
        background: "#0F1923", border: "1px solid #1e2d3d",
        borderRadius: "18px", display: "flex", flexDirection: "column",
        zIndex: 199, boxShadow: "0 10px 50px rgba(0,0,0,0.5)",
        overflow: "hidden",
        opacity: open ? 1 : 0,
        transform: open ? "translateY(0) scale(1)" : "translateY(14px) scale(0.97)",
        pointerEvents: open ? "auto" : "none",
        transition: "opacity 0.2s ease, transform 0.2s ease",
      }}>
        {/* Header */}
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #1e2d3d", display: "flex", alignItems: "center", gap: "10px", flexShrink: 0, background: "#0a1118" }}>
          <div style={{ width: "34px", height: "34px", borderRadius: "50%", background: "#1a2a3a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>{selectedAgent?.emoji || "🤖"}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: "#e8e4dc", fontWeight: 700, fontSize: "14px" }}>{selectedAgent?.name || "Agente"}</div>
            <div style={{ color: "#4a6a8a", fontSize: "11px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.name}</div>
          </div>
          {/* Selector de agente compacto */}
          {agents.length > 1 && (
            <select value={selectedAgent?.id || ""} onChange={e => switchAgent(e.target.value)}
              style={{ fontSize: "11px", padding: "4px 6px", borderRadius: "7px", border: "1px solid #1e2d3d", background: "#1a2a3a", color: "#9ab", cursor: "pointer", outline: "none" }}>
              {agents.map(a => <option key={a.id} value={a.id}>{a.emoji} {a.name}</option>)}
            </select>
          )}
          <button onClick={() => setOpen(false)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#4a6a8a", display: "flex", alignItems: "center", padding: "4px", borderRadius: "6px" }}
            onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.color = "#e8e4dc"}
            onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.color = "#4a6a8a"}>
            <X size={16} />
          </button>
        </div>

        {/* Mensajes */}
        <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: "10px" }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{
                maxWidth: "82%", padding: "10px 13px",
                borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                background: m.role === "user" ? "#C9A84C" : "#1a2a3a",
                color: m.role === "user" ? "#0F1923" : "#e8e4dc",
                fontSize: "13px", lineHeight: 1.5, wordBreak: "break-word",
              }}>
                {m.content}
              </div>
            </div>
          ))}
          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{ padding: "10px 16px", borderRadius: "16px 16px 16px 4px", background: "#1a2a3a" }}>
                <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                  {[0,1,2].map(i => (
                    <span key={i} style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#C9A84C", opacity: 0.7, animation: `bounce 1.2s ${i*0.2}s infinite ease-in-out` }} />
                  ))}
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div style={{ padding: "10px 14px", borderTop: "1px solid #1e2d3d", display: "flex", gap: "8px", flexShrink: 0, background: "#0a1118" }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
            placeholder={`Pregunta a ${selectedAgent?.name || "un agente"}…`}
            disabled={loading}
            style={{ flex: 1, background: "#1a2a3a", border: "1px solid #1e2d3d", borderRadius: "10px", padding: "9px 13px", color: "#e8e4dc", fontSize: "13px", outline: "none", fontFamily: "inherit", transition: "border-color 0.15s" }}
            onFocus={e => (e.currentTarget.style.borderColor = "#C9A84C")}
            onBlur={e => (e.currentTarget.style.borderColor = "#1e2d3d")}
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            style={{ width: "38px", height: "38px", borderRadius: "10px", background: input.trim() && !loading ? "#C9A84C" : "#1a2a3a", border: "none", cursor: input.trim() && !loading ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.15s" }}
          >
            <Send size={15} color={input.trim() && !loading ? "#0F1923" : "#4a6a8a"} />
          </button>
        </div>
      </div>
    </>
  );
}


// ── NewProjectModal ──────────────────────────────────────────
function NewProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({ name: "", description: "", color: COLORS[0], due_date: "", owner: "Didac" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    await fetch(`${API}/projects`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
    setSaving(false); onCreated(); onClose();
  };
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
          <h3 style={{ fontSize: "18px", fontWeight: 700 }}>Nuevo proyecto</h3>
          <button onClick={onClose} style={iconBtnStyle}><X size={16} /></button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nombre del proyecto..." style={inputStyle} autoFocus />
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Descripción (opcional)..." rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          <input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} style={inputStyle} />
          <div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "8px" }}>Color</div>
            <div style={{ display: "flex", gap: "8px" }}>
              {COLORS.map(c => <button key={c} onClick={() => setForm({ ...form, color: c })} style={{ width: "28px", height: "28px", borderRadius: "8px", background: c, border: form.color === c ? "3px solid var(--text)" : "3px solid transparent", cursor: "pointer" }} />)}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "24px" }}>
          <button onClick={onClose} style={btnSecondaryStyle}>Cancelar</button>
          <button onClick={submit} disabled={saving || !form.name.trim()} style={{ ...btnPrimaryStyle, opacity: saving || !form.name.trim() ? 0.6 : 1, display: "flex", alignItems: "center", gap: "7px" }}>
            {saving ? <RefreshCw size={13} className="animate-spin" /> : <Plus size={13} />} Crear proyecto
          </button>
        </div>
      </div>
    </div>
  );
}

// ── KanbanColumn ─────────────────────────────────────────────
function KanbanColumn({ statusKey, tasks, agents, members, projectId, onTaskClick, onRefresh }: {
  statusKey: string; tasks: any[]; agents: any[]; members: any[]; projectId: number;
  onTaskClick: (t: any) => void; onRefresh: () => void;
}) {
  const cfg = TASK_STATUS[statusKey] || TASK_STATUS.pending;
  const STATUSES = Object.keys(TASK_STATUS);
  const moveTask = async (taskId: number, newStatus: string) => {
    await fetch(`${API}/projects/tasks/${taskId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: newStatus }) });
    onRefresh();
  };
  return (
    <div style={{ flex: 1, minWidth: "230px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", padding: "8px 12px", background: cfg.color + "12", borderRadius: "10px", border: `1px solid ${cfg.color}25` }}>
        <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: cfg.color, flexShrink: 0 }} />
        <span style={{ fontSize: "13px", fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
        <span style={{ fontSize: "12px", color: "var(--text-muted)", marginLeft: "auto", background: "var(--bg)", borderRadius: "20px", padding: "1px 7px", border: "1px solid var(--border)" }}>{tasks.length}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {tasks.map(task => {
          const pri = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
          const agent = agents.find(a => a.id === task.agent_id);
          const member = members.find(m => m.id === Number(task.assignee_id));
          const assignee = agent || member;
          const prevStatus = STATUSES[STATUSES.indexOf(statusKey) - 1];
          const nextStatus = STATUSES[STATUSES.indexOf(statusKey) + 1];
          return (
            <div key={task.id} onClick={() => onTaskClick(task)}
              style={{ background: "var(--bg-card)", borderRadius: "10px", border: "1px solid var(--border)", padding: "12px 14px", cursor: "pointer", boxShadow: "var(--shadow)", transition: "transform 100ms, box-shadow 100ms" }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(-1px)"; (e.currentTarget as HTMLElement).style.boxShadow = "var(--shadow-md)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ""; (e.currentTarget as HTMLElement).style.boxShadow = "var(--shadow)"; }}>
              <div style={{ fontSize: "13px", fontWeight: 500, marginBottom: "8px", lineHeight: 1.4 }}>{task.title}</div>
              <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "wrap", marginBottom: "8px" }}>
                <span style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "10px", background: pri.color + "20", color: pri.color }}>{pri.label}</span>
                {assignee && <span style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "10px", background: member ? member.color + "20" : "var(--bg-muted)", color: member ? member.color : "var(--text-muted)" }}>{assignee.emoji} {assignee.name}</span>}
                {task.due_date && <span style={{ fontSize: "10px", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: "2px" }}><Calendar size={9} /> {new Date(task.due_date).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}</span>}
              </div>
              {(prevStatus || nextStatus) && (
                <div style={{ display: "flex", gap: "4px", justifyContent: "flex-end" }} onClick={e => e.stopPropagation()}>
                  {prevStatus && <button onClick={() => moveTask(task.id, prevStatus)} style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "6px", background: "var(--bg-muted)", border: "1px solid var(--border)", cursor: "pointer", color: "var(--text-muted)" }}>← {TASK_STATUS[prevStatus].label}</button>}
                  {nextStatus && <button onClick={() => moveTask(task.id, nextStatus)} style={{ fontSize: "10px", padding: "2px 7px", borderRadius: "6px", background: "var(--bg-dark)", border: "none", cursor: "pointer", color: "white" }}>{TASK_STATUS[nextStatus].label} →</button>}
                </div>
              )}
            </div>
          );
        })}
        {tasks.length === 0 && <div style={{ padding: "18px", textAlign: "center", color: "var(--text-light)", fontSize: "12px", border: "2px dashed var(--border)", borderRadius: "10px" }}>Sin tareas</div>}
      </div>
    </div>
  );
}


// ── ProjectDetail ────────────────────────────────────────────
function ProjectDetail({ project, onBack, onRefresh, agents, members }: {
  project: any; onBack: () => void; onRefresh: () => void; agents: any[]; members: any[];
}) {
  const [detail, setDetail] = useState<any>(null);
  const [tab, setTab] = useState<"tasks" | "docs">("tasks");
  const [taskView, setTaskView] = useState<"kanban" | "list">("kanban");
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showDocForm, setShowDocForm] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: "", description: "", agent_id: "", assignee_id: "", priority: "medium", due_date: "" });
  const [docForm, setDocForm] = useState({ title: "", content: "" });
  const [saving, setSaving] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  
  const [description, setDescription] = useState(project.description || "");
  const [editingDesc, setEditingDesc] = useState(false);
  const [savingDesc, setSavingDesc] = useState(false);
  const [showProps, setShowProps] = useState(true);
  const [props, setProps] = useState({
    status: project.status || "active",
    owners: (() => { try { return JSON.parse(project.owners || '[]'); } catch { return []; } })() as string[],
    due_date: project.due_date ? project.due_date.split("T")[0] : "",
    priority: project.priority || "medium",
    cover_image: project.cover_image || "",
  });
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);

  const saveProp = async (key: string, value: any) => {
    await fetch(`${API}/projects/${project.id}`, {
      method: "PATCH", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [key]: value }),
    });
    onRefresh();
  };

  const uploadCover = async (file: File) => {
    setUploadingCover(true);
    const fd = new FormData();
    fd.append("file", file);
    await fetch(`${API}/projects/${project.id}/cover`, { method: "POST", body: fd });
    setProps(p => ({ ...p, cover_image: `${API}/projects/${project.id}/cover?t=${Date.now()}` }));
    setUploadingCover(false);
    onRefresh();
  };

  // Calcular progreso
  const totalTasks = detail?.tasks?.length || 0;
  const doneTasks = detail?.tasks?.filter((t: any) => t.status === 'done').length || 0;
  const progress = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  const loadDetail = () => {
    fetch(`${API}/projects/${project.id}`).then(r => r.json()).then(setDetail).catch(() => {});
  };
  useEffect(() => { loadDetail(); }, [project.id]);

  const saveDescription = async () => {
    setEditingDesc(false);
    await fetch(`${API}/projects/${project.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ description }) });
    onRefresh();
  };

  const createTask = async () => {
    if (!taskForm.title) return;
    setSaving(true);
    await fetch(`${API}/projects/${project.id}/tasks`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...taskForm, agent_id: taskForm.agent_id || null, assignee_id: taskForm.assignee_id || null }),
    });
    setSaving(false); setShowTaskForm(false);
    setTaskForm({ title: "", description: "", agent_id: "", assignee_id: "", priority: "medium", due_date: "" });
    loadDetail(); onRefresh();
  };

  const createDoc = async () => {
    if (!docForm.title) return;
    setSaving(true);
    await fetch(`${API}/projects/${project.id}/docs`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(docForm) });
    setSaving(false); setShowDocForm(false); setDocForm({ title: "", content: "" }); loadDetail();
  };

  const deleteProject = async () => {
    if (!confirm("¿Eliminar este proyecto?")) return;
    await fetch(`${API}/projects/${project.id}`, { method: "DELETE" });
    onBack(); onRefresh();
  };

  const cfg = STATUS_CONFIG[project.status] || STATUS_CONFIG.active;
  const filteredTasks = (detail?.tasks || []).filter((t: any) => {
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase());
    const matchAssignee = !filterAssignee ||
      (filterAssignee.startsWith('agent:') && String(t.agent_id) === filterAssignee.replace('agent:', '')) ||
      (filterAssignee.startsWith('member:') && String(t.assignee_id) === filterAssignee.replace('member:', ''));
    const matchStatus = !filterStatus || t.status === filterStatus;
    return matchSearch && matchAssignee && matchStatus;
  });
  const tasksByStatus = (s: string) => filteredTasks.filter((t: any) => t.status === s);

  return (
    <div className="animate-fade-in" style={{ width: "100%" }}>
      {/* Breadcrumb */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "13px" }}>← Proyectos</button>
        <ChevronRight size={14} color="var(--text-light)" />
        <span style={{ fontSize: "13px", color: "var(--text-muted)" }}>{project.name}</span>
      </div>

      {/* Header del proyecto — estilo Notion */}
      <div style={{ background: "var(--bg-card)", borderRadius: "16px", border: "1px solid var(--border)", overflow: "hidden", marginBottom: "20px", boxShadow: "var(--shadow)" }}>

        {/* Imagen de portada */}
        <div style={{ position: "relative", height: props.cover_image ? "160px" : "6px", background: project.color || "var(--accent)", overflow: "hidden" }}>
          {props.cover_image && (
            <img src={API + "/projects/" + project.id + "/cover?t=" + Date.now()} alt="portada" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          )}
          <div style={{ position: "absolute", bottom: "8px", right: "12px", display: "flex", gap: "6px" }}>
            <input ref={coverInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files; if (f && f[0]) uploadCover(f[0]); }} />
            <button onClick={() => coverInputRef.current?.click()} style={{ padding: "5px 10px", background: "rgba(0,0,0,0.5)", color: "white", border: "none", borderRadius: "7px", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" } as React.CSSProperties}>
              {uploadingCover ? <RefreshCw size={11} className="animate-spin" /> : <Upload size={11} />}
              {props.cover_image ? "Cambiar portada" : "Añadir portada"}
            </button>
          </div>
        </div>

        <div style={{ padding: "20px 24px" }}>
          {/* Nombre + acciones */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "16px" }}>
            <h1 style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.2, flex: 1 }}>{project.name}</h1>
            <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>

              <button onClick={deleteProject} style={{ ...iconBtnStyle, color: "#ef4444" }}><Trash2 size={14} /></button>
            </div>
          </div>

          {/* Barra de progreso */}
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "var(--text-muted)", marginBottom: "5px" }}>
              <span>Progreso — {doneTasks}/{totalTasks} tareas completadas</span>
              <span style={{ fontWeight: 600, color: progress === 100 ? "#22c55e" : "var(--text-muted)" }}>{progress}%</span>
            </div>
            <div style={{ height: "6px", background: "var(--bg-muted)", borderRadius: "6px", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${progress}%`, background: progress === 100 ? "#22c55e" : "var(--accent)", borderRadius: "6px", transition: "width 0.6s ease" }} />
            </div>
          </div>

          {/* Propiedades estilo Notion */}
          <div style={{ marginBottom: "16px" }}>
            <button onClick={() => setShowProps(v => !v)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "11px", color: "var(--text-light)", display: "flex", alignItems: "center", gap: "4px", marginBottom: "8px", padding: 0 }}>
              <ChevronDown size={12} style={{ transform: showProps ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 150ms" }} />
              {showProps ? "Ocultar propiedades" : "Mostrar propiedades"}
            </button>

            {showProps && (
              <div style={{ border: "1px solid var(--border)", borderRadius: "10px", overflow: "hidden" }}>

                {/* Estado */}
                <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: "130px", padding: "9px 14px", fontSize: "12px", color: "var(--text-muted)", fontWeight: 500, background: "var(--bg)", borderRight: "1px solid var(--border)", flexShrink: 0 }}>Estado</div>
                  <select value={props.status} onChange={e => { setProps(p => ({ ...p, status: e.target.value })); saveProp("status", e.target.value); }}
                    style={{ flex: 1, padding: "9px 14px", border: "none", background: "transparent", fontSize: "13px", color: STATUS_CONFIG[props.status]?.color || "var(--text)", cursor: "pointer", outline: "none", fontFamily: "inherit", fontWeight: 600 }}>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>

                {/* Prioridad */}
                <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: "130px", padding: "9px 14px", fontSize: "12px", color: "var(--text-muted)", fontWeight: 500, background: "var(--bg)", borderRight: "1px solid var(--border)", flexShrink: 0 }}>Prioridad</div>
                  <select value={props.priority} onChange={e => { setProps(p => ({ ...p, priority: e.target.value })); saveProp("priority", e.target.value); }}
                    style={{ flex: 1, padding: "9px 14px", border: "none", background: "transparent", fontSize: "13px", color: PRIORITY_CONFIG[props.priority]?.color || "var(--text)", cursor: "pointer", outline: "none", fontFamily: "inherit", fontWeight: 600 }}>
                    {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>

                {/* Responsables (multi) */}
                <div style={{ display: "flex", alignItems: "flex-start", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ width: "130px", padding: "9px 14px", fontSize: "12px", color: "var(--text-muted)", fontWeight: 500, background: "var(--bg)", borderRight: "1px solid var(--border)", flexShrink: 0, paddingTop: "10px" }}>Responsables</div>
                  <div style={{ flex: 1, padding: "8px 14px" }}>
                    {/* Pills de responsables seleccionados */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px", marginBottom: props.owners.length ? "6px" : "0" }}>
                      {props.owners.map((o: string) => (
                        <span key={o} style={{ display: "flex", alignItems: "center", gap: "4px", padding: "3px 8px", background: "var(--bg-muted)", borderRadius: "20px", fontSize: "12px", border: "1px solid var(--border)" }}>
                          {o}
                          <button onClick={() => { const next = props.owners.filter((x: string) => x !== o); setProps(p => ({ ...p, owners: next })); saveProp("owners", next); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 0, fontSize: "14px", lineHeight: 1 }}>×</button>
                        </span>
                      ))}
                    </div>
                    <select defaultValue="" onChange={e => {
                      const val = e.target.value;
                      if (!val || props.owners.includes(val)) return;
                      const next = [...props.owners, val];
                      setProps(p => ({ ...p, owners: next }));
                      saveProp("owners", next);
                      e.target.value = "";
                    }} style={{ fontSize: "12px", color: "var(--text-muted)", background: "transparent", border: "none", outline: "none", cursor: "pointer", fontFamily: "inherit" }}>
                      <option value="">+ Añadir responsable...</option>
                      {agents.map(a => <option key={a.id} value={`${a.emoji} ${a.name}`}>{a.emoji} {a.name}</option>)}
                      {members.map(m => <option key={m.id} value={`${m.emoji} ${m.name}`}>{m.emoji} {m.name}</option>)}
                    </select>
                  </div>
                </div>

                {/* Fecha fin */}
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ width: "130px", padding: "9px 14px", fontSize: "12px", color: "var(--text-muted)", fontWeight: 500, background: "var(--bg)", borderRight: "1px solid var(--border)", flexShrink: 0 }}>Fecha fin</div>
                  <input type="date" value={props.due_date} onChange={e => { setProps(p => ({ ...p, due_date: e.target.value })); saveProp("due_date", e.target.value); }}
                    style={{ flex: 1, padding: "9px 14px", border: "none", background: "transparent", fontSize: "13px", color: "var(--text)", cursor: "pointer", outline: "none", fontFamily: "inherit" }} />
                </div>

              </div>
            )}
          </div>

          {/* Descripción */}
          <div onClick={() => setEditingDesc(true)} style={{ cursor: "text" }}>
            {editingDesc ? (
              <textarea value={description} onChange={e => setDescription(e.target.value)} autoFocus
                onBlur={saveDescription} rows={3}
                style={{ ...inputStyle, fontSize: "14px", resize: "vertical", lineHeight: 1.6 }}
                placeholder="Añade una descripción..." />
            ) : (
              <div style={{ fontSize: "14px", color: description ? "var(--text-muted)" : "var(--text-light)", lineHeight: 1.6, minHeight: "28px", padding: "2px 0" }}>
                {description || <span style={{ fontStyle: "italic" }}>Añade una descripción...</span>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs principales */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
        <div style={{ display: "flex", gap: "0", borderBottom: "2px solid var(--border)" }}>
          <button onClick={() => setTab("tasks")} style={{ padding: "8px 18px", fontSize: "13px", fontWeight: tab === "tasks" ? 600 : 400, cursor: "pointer", border: "none", background: "transparent", color: tab === "tasks" ? "var(--text)" : "var(--text-muted)", borderBottom: tab === "tasks" ? "2px solid var(--accent)" : "2px solid transparent", marginBottom: "-2px" }}>
            Tareas ({detail?.tasks?.length || 0})
          </button>
          <button onClick={() => setTab("docs")} style={{ padding: "8px 18px", fontSize: "13px", fontWeight: tab === "docs" ? 600 : 400, cursor: "pointer", border: "none", background: "transparent", color: tab === "docs" ? "var(--text)" : "var(--text-muted)", borderBottom: tab === "docs" ? "2px solid var(--accent)" : "2px solid transparent", marginBottom: "-2px" }}>
            Docs ({detail?.docs?.length || 0})
          </button>
        </div>

        {tab === "tasks" && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", background: "var(--bg-muted)", borderRadius: "8px", padding: "2px" }}>
              <button onClick={() => setTaskView("kanban")} style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "12px", border: "none", cursor: "pointer", background: taskView === "kanban" ? "var(--bg-card)" : "transparent", color: taskView === "kanban" ? "var(--text)" : "var(--text-muted)", fontWeight: taskView === "kanban" ? 600 : 400, display: "flex", alignItems: "center", gap: "4px" }}>
                <LayoutGrid size={13} /> Kanban
              </button>
              <button onClick={() => setTaskView("list")} style={{ padding: "5px 12px", borderRadius: "6px", fontSize: "12px", border: "none", cursor: "pointer", background: taskView === "list" ? "var(--bg-card)" : "transparent", color: taskView === "list" ? "var(--text)" : "var(--text-muted)", fontWeight: taskView === "list" ? 600 : 400, display: "flex", alignItems: "center", gap: "4px" }}>
                <List size={13} /> Lista
              </button>
            </div>
            <div style={{ position: "relative" }}>
              <Search size={12} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." style={{ ...inputStyle, paddingLeft: "28px", fontSize: "12px", padding: "7px 12px 7px 28px", width: "160px" }} />
            </div>
            <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)} style={{ ...inputStyle, width: "auto", minWidth: "120px", fontSize: "12px", padding: "7px 10px" }}>
              <option value="">Todos</option>
              {agents.length > 0 && <optgroup label="Agentes IA">{agents.map(a => <option key={`agent:${a.id}`} value={`agent:${a.id}`}>{a.emoji} {a.name}</option>)}</optgroup>}
              {members.length > 0 && <optgroup label="Equipo">{members.map(m => <option key={`member:${m.id}`} value={`member:${m.id}`}>{m.emoji} {m.name}</option>)}</optgroup>}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, width: "auto", minWidth: "115px", fontSize: "12px", padding: "7px 10px" }}>
              <option value="">Todos los estados</option>
              {Object.entries(TASK_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button onClick={() => setShowTaskForm(v => !v)} style={{ ...btnPrimaryStyle, display: "flex", alignItems: "center", gap: "6px", padding: "7px 14px", fontSize: "12px" }}>
              <Plus size={13} /> Nueva tarea
            </button>
          </div>
        )}
      </div>

      {/* Form nueva tarea */}
      {showTaskForm && tab === "tasks" && (
        <div style={{ background: "var(--bg-card)", borderRadius: "12px", border: "1px solid var(--border)", padding: "18px", marginBottom: "14px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} placeholder="Título de la tarea..." style={inputStyle} autoFocus />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
              <AssigneeSelect agents={agents} members={members} agentId={taskForm.agent_id} assigneeId={taskForm.assignee_id} onChange={(type, id) => setTaskForm(f => ({ ...f, agent_id: type === 'agent' ? id : '', assignee_id: type === 'member' ? id : '' }))} />
              <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })} style={inputStyle}>
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <input type="date" value={taskForm.due_date} onChange={e => setTaskForm({ ...taskForm, due_date: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
              <button onClick={() => setShowTaskForm(false)} style={btnSecondaryStyle}>Cancelar</button>
              <button onClick={createTask} disabled={saving || !taskForm.title} style={{ ...btnPrimaryStyle, opacity: saving || !taskForm.title ? 0.6 : 1, display: "flex", alignItems: "center", gap: "6px" }}>
                {saving ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />} Crear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAREAS */}
      {tab === "tasks" && taskView === "kanban" && (
        <div style={{ display: "flex", gap: "12px", overflowX: "auto", paddingBottom: "16px" }}>
          {Object.keys(TASK_STATUS).map(s => (
            <KanbanColumn key={s} statusKey={s} tasks={filteredTasks.filter((t: any) => t.status === s)} agents={agents} members={members} projectId={project.id}
              onTaskClick={task => setSelectedTask({ ...task, subtasks: task.subtasks || [], comments: task.comments || [] })} onRefresh={loadDetail} />
          ))}
        </div>
      )}

      {tab === "tasks" && taskView === "list" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {!filteredTasks.length && <div style={{ textAlign: "center", padding: "40px", color: "var(--text-light)", fontSize: "13px" }}>Sin tareas</div>}
          {filteredTasks.map((task: any) => {
            const s = TASK_STATUS[task.status] || TASK_STATUS.pending;
            const pri = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.medium;
            const agent = agents.find(a => a.id === task.agent_id);
            const member = members.find(m => m.id === Number(task.assignee_id));
            const assignee = agent || member;
            return (
              <div key={task.id} onClick={() => setSelectedTask({ ...task, subtasks: task.subtasks || [], comments: task.comments || [] })}
                style={{ background: "var(--bg-card)", borderRadius: "10px", border: "1px solid var(--border)", padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px", cursor: "pointer" }}
                onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = "var(--bg)"}
                onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = "var(--bg-card)"}>
                <span style={{ width: "8px", height: "8px", borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: "13px", fontWeight: 500 }}>{task.title}</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "2px", display: "flex", gap: "8px" }}>
                    {assignee && <span style={{ color: member ? member.color : undefined }}>{assignee.emoji} {assignee.name}</span>}
                    {task.due_date && <span style={{ display: "flex", alignItems: "center", gap: "3px" }}><Calendar size={10} /> {new Date(task.due_date).toLocaleDateString("es-ES")}</span>}
                    {task.subtasks?.length > 0 && <span><CheckSquare size={10} style={{ display: "inline" }} /> {task.subtasks.filter((s: any) => s.status === "done").length}/{task.subtasks.length}</span>}
                  </div>
                </div>
                <span style={{ fontSize: "10px", padding: "2px 8px", borderRadius: "10px", background: pri.color + "20", color: pri.color }}>{pri.label}</span>
                <span style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "20px", background: s.color + "18", color: s.color }}>{s.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* DOCS */}
      {tab === "docs" && (
        <div>
          <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "12px" }}>
            Guardado en <code style={{ background: "var(--bg-muted)", padding: "2px 6px", borderRadius: "4px", fontSize: "11px" }}>Escritorio/Varadero HQ/{project.name}</code>
          </div>
          <ProjectUploadZone projectId={project.id} onUploaded={loadDetail} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
            <button onClick={() => setShowDocForm(v => !v)} style={{ ...btnSecondaryStyle, padding: "7px 14px", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px" }}><Plus size={12} /> Nota de texto</button>
          </div>
          {showDocForm && (
            <div style={{ background: "var(--bg-card)", borderRadius: "12px", border: "1px solid var(--border)", padding: "18px", marginBottom: "12px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <input value={docForm.title} onChange={e => setDocForm({ ...docForm, title: e.target.value })} placeholder="Título..." style={inputStyle} autoFocus />
                <textarea value={docForm.content} onChange={e => setDocForm({ ...docForm, content: e.target.value })} placeholder="Contenido..." rows={4} style={{ ...inputStyle, resize: "vertical" }} />
                <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                  <button onClick={() => setShowDocForm(false)} style={btnSecondaryStyle}>Cancelar</button>
                  <button onClick={createDoc} disabled={saving || !docForm.title} style={{ ...btnPrimaryStyle, opacity: saving || !docForm.title ? 0.6 : 1 }}>Guardar</button>
                </div>
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {!detail?.docs?.length && <div style={{ textAlign: "center", padding: "30px", color: "var(--text-light)", fontSize: "13px" }}>Sin documentos todavía</div>}
            {detail?.docs?.map((doc: any) => (
              <div key={doc.id} style={{ background: "var(--bg-card)", borderRadius: "10px", border: "1px solid var(--border)", padding: "12px 16px", display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "34px", height: "34px", borderRadius: "8px", background: "var(--bg)", border: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "16px" }}>
                  {doc.file_type?.includes("pdf") ? "📕" : doc.file_type?.startsWith("image/") ? "🖼️" : doc.file_type?.includes("sheet") ? "📊" : doc.file_path ? "📄" : <FileText size={14} color="var(--accent)" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "13px", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.title}</div>
                  <div style={{ fontSize: "11px", color: "var(--text-muted)", display: "flex", gap: "8px" }}>
                    {doc.file_size && <span>{doc.file_size > 1024*1024 ? `${(doc.file_size/(1024*1024)).toFixed(1)} MB` : `${Math.round(doc.file_size/1024)} KB`}</span>}
                    <span>{new Date(doc.updated_at || doc.created_at).toLocaleDateString("es-ES")}</span>
                  </div>
                </div>
                {doc.file_path && <a href={`${API}/projects/docs/${doc.id}/download`} target="_blank" style={{ ...iconBtnStyle, color: "var(--accent)", textDecoration: "none" }}><Download size={13} /></a>}
                <button onClick={async () => { if (!confirm("Eliminar?")) return; await fetch(`${API}/projects/${project.id}/docs/${doc.id}`, { method: "DELETE" }); loadDetail(); }} style={{ ...iconBtnStyle, color: "#ef4444" }}><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedTask && <TaskPanel task={selectedTask} agents={agents} members={members} projectId={project.id} onClose={() => setSelectedTask(null)} onUpdated={() => { loadDetail(); setSelectedTask(null); }} />}
      <AgentChatWidget project={project} agents={agents} onProjectUpdated={() => { loadDetail(); onRefresh(); }} />
    </div>
  );
}

// ── ProjectGridCard ────────────────────────────────────────
function ProjectGridCard({ project, agents, members, onClick }: { project: any; agents: any[]; members: any[]; onClick: () => void }) {
  const progress = project.task_count > 0 ? Math.round(((project.tasks_done || 0) / project.task_count) * 100) : 0;
  const barColor = progress > 0 ? (project.color || "var(--acc)") : "var(--tx3)";
  return (
    <div onClick={onClick}
      style={{ background: "var(--panel)", borderRadius: "14px", border: "1px solid var(--line)", overflow: "hidden", cursor: "pointer", transition: "transform 140ms, border-color 140ms", display: "flex", flexDirection: "column" }}
      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = "translateY(-2px)"; (e.currentTarget as HTMLElement).style.borderColor = "var(--tx3)"; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = ""; (e.currentTarget as HTMLElement).style.borderColor = "var(--line)"; }}>
      {/* Franja superior de color */}
      <div style={{ height: "5px", background: project.color || "var(--acc)", flexShrink: 0 }} />
      <div style={{ padding: "18px", display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
        {/* Icono + nombre */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <div style={{ width: "42px", height: "42px", borderRadius: "10px", background: (project.color || "#C9A84C") + "22", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "22px", flexShrink: 0 }}>
            {project.emoji || "📁"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "14px", lineHeight: 1.3, color: "var(--tx)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.name}</div>
            {project.description && (
              <div style={{ fontSize: "11.5px", color: "var(--tx3)", marginTop: "3px", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden" }}>
                {project.description}
              </div>
            )}
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: "flex", gap: "12px", fontSize: "11px", color: "var(--tx3)" }}>
          <span>✓ {project.tasks_done || 0}/{project.task_count || 0} tareas</span>
          {(project.doc_count || 0) > 0 && <span>📄 {project.doc_count} docs</span>}
        </div>

        {/* Barra de progreso — protagonista */}
        <div style={{ marginTop: "auto" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
            <span style={{ fontSize: "10.5px", color: progress > 0 ? "var(--tx2)" : "var(--tx3)" }}>
              {progress > 0 ? "En marcha" : "Por arrancar"}
            </span>
            <span style={{ fontSize: "13px", fontWeight: 700, color: progress > 0 ? (project.color || "var(--acc)") : "var(--tx3)", fontFamily: "'JetBrains Mono', monospace" }}>
              {progress}%
            </span>
          </div>
          <div style={{ height: "7px", background: "var(--panel2)", borderRadius: "6px", overflow: "hidden", border: "1px solid var(--line)" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: barColor, borderRadius: "6px", transition: "width 0.6s ease", minWidth: progress > 0 ? "4px" : "0" }} />
          </div>
        </div>
      </div>
    </div>
  );
}
// ── ProjectTableRow ──────────────────────────────────────────
function ProjectTableRow({ project, agents, members, onClick }: { project: any; agents: any[]; members: any[]; onClick: () => void }) {
  const cfg = STATUS_CONFIG[project.status] || STATUS_CONFIG.active;
  const progress = project.task_count > 0 ? Math.round((project.tasks_done / project.task_count) * 100) : 0;
  return (
    <tr onClick={onClick} style={{ cursor: "pointer", borderBottom: "1px solid var(--border)" }}
      onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = "var(--bg)"}
      onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = ""}>
      <td style={{ padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: project.color || "var(--accent)", flexShrink: 0 }} />
        <span style={{ fontSize: "13px", fontWeight: 600 }}>{project.name}</span>
      </td>
      <td style={{ padding: "12px 16px" }}><span style={{ fontSize: "11px", padding: "3px 9px", borderRadius: "20px", background: cfg.color + "18", color: cfg.color, fontWeight: 500 }}>{cfg.label}</span></td>
      <td style={{ padding: "12px 16px", fontSize: "12px", color: "var(--text-muted)" }}>{project.owner || "—"}</td>
      <td style={{ padding: "12px 16px", fontSize: "12px", color: "var(--text-muted)" }}>{project.due_date ? new Date(project.due_date).toLocaleDateString("es-ES") : "—"}</td>
      <td style={{ padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div style={{ flex: 1, height: "5px", background: "var(--bg-muted)", borderRadius: "3px", minWidth: "60px" }}>
            <div style={{ height: "100%", width: `${progress}%`, background: project.color || "var(--accent)", borderRadius: "3px" }} />
          </div>
          <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{progress}%</span>
        </div>
      </td>
      <td style={{ padding: "12px 16px", fontSize: "12px", color: "var(--text-muted)" }}>{project.task_count || 0}</td>
    </tr>
  );
}

// ── Página principal ─────────────────────────────────────────
export default function ProjectsPage() {
  const router = useRouter();
  const [projects, setProjects] = useState<any[]>([]);
  const [agents, setAgents] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [showNew, setShowNew] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "table">("grid");
  const [search, setSearch] = useState("");

  const load = () => {
    fetch(`${API}/projects-v2`).then(r => r.json()).then(setProjects).catch(() => {});
    fetch(`${API}/agents`).then(r => r.json()).then(setAgents).catch(() => {});
    fetch(`${API}/team-members`).then(r => r.json()).then(setMembers).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const filtered = projects.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <div className="animate-fade-in" style={{ width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" }}>
          <div>
            <h1 style={{ fontSize: "26px", fontWeight: 700, color: "var(--tx)" }}>Proyectos</h1>
            <p style={{ color: "var(--tx3)", marginTop: "4px", fontSize: "13px" }}>
              {projects.length} proyectos
              {" · "}
              {projects.filter(p => (p.tasks_done || 0) > 0 && p.tasks_done < p.task_count).length} en marcha
              {" · "}
              {projects.filter(p => !p.tasks_done || p.tasks_done === 0).length} por arrancar
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <div style={{ position: "relative" }}>
              <Search size={13} style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar proyectos..." style={{ ...inputStyle, paddingLeft: "30px", width: "200px", fontSize: "12px" }} />
            </div>
            <div style={{ display: "flex", background: "var(--bg-muted)", borderRadius: "10px", padding: "3px" }}>
              <button onClick={() => setViewMode("grid")} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "none", background: viewMode === "grid" ? "var(--bg-card)" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: viewMode === "grid" ? "var(--text)" : "var(--text-muted)", boxShadow: viewMode === "grid" ? "var(--shadow)" : "none" }}>
                <LayoutGrid size={15} />
              </button>
              <button onClick={() => setViewMode("table")} style={{ width: "32px", height: "32px", borderRadius: "8px", border: "none", background: viewMode === "table" ? "var(--bg-card)" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: viewMode === "table" ? "var(--text)" : "var(--text-muted)", boxShadow: viewMode === "table" ? "var(--shadow)" : "none" }}>
                <List size={15} />
              </button>
            </div>
            <button onClick={() => setShowNew(true)} style={{ ...btnPrimaryStyle, display: "flex", alignItems: "center", gap: "7px" }}>
              <Plus size={14} /> Nuevo proyecto
            </button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px", color: "var(--text-light)" }}>
            <FolderOpen size={48} style={{ margin: "0 auto 16px", display: "block", opacity: 0.2 }} />
            <p style={{ fontSize: "15px" }}>Sin proyectos todavía</p>
            <button onClick={() => setShowNew(true)} style={{ ...btnPrimaryStyle, marginTop: "20px", display: "inline-flex", alignItems: "center", gap: "7px" }}><Plus size={14} /> Crear proyecto</button>
          </div>
        ) : viewMode === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "16px" }} className="projects-grid">
            {filtered.map(p => <ProjectGridCard key={p.id} project={p} agents={agents} members={members} onClick={() => router.push(`/projects/${p.id}`)} />)}
          </div>
        ) : (
          <div style={{ background: "var(--bg-card)", borderRadius: "14px", border: "1px solid var(--border)", overflow: "hidden", boxShadow: "var(--shadow)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
                  {["Proyecto", "Estado", "Responsable", "Fecha límite", "Progreso", "Tareas"].map(h => (
                    <th key={h} style={{ padding: "10px 16px", fontSize: "11px", fontWeight: 600, color: "var(--text-muted)", textAlign: "left", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => <ProjectTableRow key={p.id} project={p} agents={agents} members={members} onClick={() => router.push(`/projects/${p.id}`)} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showNew && <NewProjectModal onClose={() => setShowNew(false)} onCreated={load} />}
    </>
  );
}
